import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { TemplateCard } from './pdf/TemplateCard';
import { AllTemplateSettings, TemplateStyle, defaultSettings, templates } from './pdf/templateData';
import { Column } from '@/utils/pdf-templates/types';

interface OldTemplateStyle {
  colors: { [key: string]: string };
  sections: { id: string; name: string }[];
}

export const PdfTemplateSettings = () => {
  const [settings, setSettings] = useState<AllTemplateSettings>(defaultSettings);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const savedSettings = localStorage.getItem('pdfTemplateSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        const mergedSettings: AllTemplateSettings = { ...defaultSettings };

        for (const templateId in defaultSettings) {
            if (parsed[templateId]) {
                const savedTemplateStyle = parsed[templateId];
                let columns = savedTemplateStyle.columns;

                // Migration from old `sections` array to handle legacy settings
                if (!columns && savedTemplateStyle.sections) {
                    columns = [{ id: 'main', sections: savedTemplateStyle.sections }];
                }

                mergedSettings[templateId] = {
                    ...defaultSettings[templateId], // Ensure all default keys are present
                    ...savedTemplateStyle,          // Apply all saved settings over defaults
                    columns: columns || defaultSettings[templateId].columns, // Apply migrated/saved columns
                };
            }
        }
        setSettings(mergedSettings);
      } catch (error) {
        console.error("Failed to parse PDF settings from localStorage", error);
        setSettings(defaultSettings);
      }
    }
  }, []);

  const updateTemplateSetting = (templateId: string, newSettings: Partial<TemplateStyle>) => {
    setSettings(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        ...newSettings,
      },
    }));
    setIsDirty(true);
  };

  const resetTemplateSettings = (templateId: string) => {
    setSettings(prev => ({
        ...prev,
        [templateId]: defaultSettings[templateId]
    }));
    setIsDirty(true);
    const templateName = templates.find(t => t.id === templateId)?.name;
    toast.info(`تمت إعادة تعيين إعدادات "${templateName}"`);
  }

  const saveSettings = () => {
    localStorage.setItem('pdfTemplateSettings', JSON.stringify(settings));
    toast.success('تم حفظ إعدادات نماذج PDF بنجاح!');
    setIsDirty(false);
  };

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {templates.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            settings={settings[template.id]}
            onUpdate={(newSettings) => updateTemplateSetting(template.id, newSettings)}
            onReset={() => resetTemplateSettings(template.id)}
          />
        ))}
      </div>
      <div className="mt-8 flex justify-end">
        <Button onClick={saveSettings} disabled={!isDirty}>
          <Save className="w-4 h-4 ml-2" />
          حفظ كل التغييرات
        </Button>
      </div>
    </div>
  );
};
